import type { CSSProperties } from 'react'
import { useEffect } from 'react'
import { useTranslation } from '../../lib/i18n'

export type TooltipKey = 'accuracy' | 'accuracy_how' | 'weight' | 'tempo' | 'plateau' | 'forecast' | 'corridor' | 'progress'

interface TooltipProps {
  tooltipKey: TooltipKey | null
  onClose: () => void
}

export function Tooltip({ tooltipKey, onClose }: TooltipProps) {
  const { t } = useTranslation()

  // Scroll lock
  useEffect(() => {
    if (tooltipKey) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [tooltipKey])

  if (!tooltipKey) return null

  const title = t(`tooltip.${tooltipKey}.title`)
  const text  = t(`tooltip.${tooltipKey}.text`)

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <h3 style={styles.title}>{title}</h3>
        <p style={styles.text}>{text}</p>
        <button style={styles.btn} onClick={onClose}>{t('tooltip.btn_ok')}</button>
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'flex-end',
  },
  sheet: {
    width: '100%',
    background: 'var(--tg-theme-bg-color, #fff)',
    borderRadius: '16px 16px 0 0',
    padding: '12px 20px 32px',
    boxSizing: 'border-box',
    animation: 'slideUp 0.2s ease-out',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: 'var(--tg-theme-hint-color, #ccc)',
    margin: '0 auto 16px',
  },
  title: {
    margin: '0 0 8px',
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--tg-theme-text-color, #000)',
  },
  text: {
    margin: '0 0 20px',
    fontSize: 15,
    lineHeight: 1.5,
    color: 'var(--tg-theme-hint-color, #666)',
  },
  btn: {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    border: 'none',
    background: 'var(--tg-theme-button-color, #007aff)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
}
